import { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '../../styles/lib/utils';
import { generateColorShapeMaps, getHitShapeKey, HitDetectionCanvas } from './HitDetectionCanvas';
import { isValidLine, renderLine } from './Shapes/Line';
import { renderPoint } from './Shapes/Point';
import { isValidPolygon, renderPolygon } from './Shapes/Polygon';
import {
  DrawingCanvasMode,
  type DrawingCanvasSelectedFeature,
  type DrawingCanvasShape,
  type DrawingCanvasShapeKey,
  type DrawingCanvasShapes,
  DrawingCanvasShapeType,
  type Point,
} from './types';
import { useDrawing } from './useDrawingHook';
import { useEditing } from './useEditingHook';
import { drawingShapesSortFunc, getCanvasCoordsFromEvent, getNextOrder, scaleCanvasForHighResDisplay } from './utils';

export type DrawingCanvasProps = Omit<
  React.ComponentProps<'canvas'>,
  | 'width'
  | 'height'
  | 'ref'
  | 'onKeyDown'
  | 'onPointerDown'
  | 'onPointerMove'
  | 'onPointerUp'
  | 'onPointerCancel'
  | 'onPointerLeave'
> & {
  width: number;
  height: number;
  drawingShapes: DrawingCanvasShapes;
  mode?: DrawingCanvasMode;
  drawingModeShapeType?: DrawingCanvasShapeType.LINE | DrawingCanvasShapeType.POLYGON;
  onModeChange?: (mode: DrawingCanvasMode) => void;
  onShapeCreate?: (shape: DrawingCanvasShape) => void;
  onShapeChange?: (shape: DrawingCanvasShape) => void;
  onShapeDelete?: (key: DrawingCanvasShapeKey) => void;
  onBackgroundPointerDown?: (event: React.PointerEvent<HTMLCanvasElement>) => void;
  showHitCanvas?: boolean;
  'aria-label': string;
  'data-testid'?: string;
};

/** Draws lines, polygons and points; lets users select them, draw lines and polygons, and edit their vertices. */
export const DrawingCanvas = ({
  width,
  height,
  className,
  drawingShapes,
  mode = DrawingCanvasMode.EDIT,
  drawingModeShapeType,
  onModeChange,
  onShapeCreate,
  onShapeChange,
  onShapeDelete,
  onBackgroundPointerDown,
  showHitCanvas = false,
  ...props
}: DrawingCanvasProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const hitCanvasRef = useRef<HTMLCanvasElement>(null);
  const [selectedShapeKey, setSelectedShapeKey] = useState<DrawingCanvasShapeKey | undefined>(undefined);

  const selectedShape = selectedShapeKey ? drawingShapes[selectedShapeKey] : undefined;

  const {
    draftPoints,
    cursorPoint,
    handlePointerUp: handleDrawingPointerUp,
    handlePointerMove: handleDrawingPointerMove,
    handlePointerLeave: handleDrawingPointerLeave,
    handleKeyDown: handleDrawingKeyDown,
  } = useDrawing({
    isDrawing: mode === DrawingCanvasMode.DRAW,
    drawingShapeType: drawingModeShapeType,
    onFinish: (points) => {
      if (drawingModeShapeType) {
        const key = crypto.randomUUID();
        const potentialNewShape = {
          key,
          type: drawingModeShapeType,
          points,
          order: getNextOrder(drawingShapes),
        };
        if (isValidLine(potentialNewShape) || isValidPolygon(potentialNewShape)) {
          onShapeCreate?.(potentialNewShape);
          setSelectedShapeKey(key);
        }
      }
      onModeChange?.(DrawingCanvasMode.EDIT);
    },
  });

  useEffect(() => {
    if (mode !== DrawingCanvasMode.EDIT) setSelectedShapeKey(undefined);
  }, [mode]);

  const {
    selectedShapeWithEdits,
    bBoxFeatures,
    vertexFeatures,
    newVertexPoint,
    hoveredKey,
    handlePointerDown: handleEditingPointerDown,
    handlePointerMove: handleEditingPointerMove,
    handlePointerUp: handleEditingPointerUp,
    handlePointerCancel: handleEditingPointerCancel,
    handlePointerLeave: handleEditingPointerLeave,
    handleKeyDown: handleEditingKeyDown,
  } = useEditing({
    selectedShape,
    onShapeChange,
    onShapeDelete: (key) => {
      onShapeDelete?.(key);
      setSelectedShapeKey(undefined);
    },
  });

  const sortedDrawingShapes = useMemo((): (DrawingCanvasShape | DrawingCanvasSelectedFeature)[] => {
    const sortedShapes = Object.values(drawingShapes)
      .filter((shape) => shape.key !== selectedShapeKey)
      .sort(drawingShapesSortFunc)
      .map((shape) =>
        mode === DrawingCanvasMode.EDIT && shape.key === hoveredKey
          ? { ...shape, style: { ...shape.style, isHighlighted: true } }
          : shape,
      );
    if (!selectedShapeWithEdits) return sortedShapes;
    // The selection draws last, with the shape above its filled box so clicks on the hit canvas still reach it
    return [...sortedShapes, ...Object.values(bBoxFeatures), selectedShapeWithEdits, ...Object.values(vertexFeatures)];
  }, [drawingShapes, selectedShapeKey, selectedShapeWithEdits, bBoxFeatures, vertexFeatures, hoveredKey, mode]);

  const { colorsToKeys, keysToColors } = useMemo(
    () => generateColorShapeMaps(sortedDrawingShapes.map((shape) => shape.key)),
    [sortedDrawingShapes],
  );

  const getShapeKeyAt = (point: Point): DrawingCanvasShapeKey | undefined => {
    const hitCanvasContext = hitCanvasRef.current?.getContext('2d', { willReadFrequently: true });
    return hitCanvasContext ? getHitShapeKey(point, hitCanvasContext, colorsToKeys) : undefined;
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (event.button !== 0) return;
    const point = getCanvasCoordsFromEvent(event, event.currentTarget);
    switch (mode) {
      case DrawingCanvasMode.DRAW:
        break;
      case DrawingCanvasMode.EDIT: {
        const clickedShapeKey = getShapeKeyAt(point);
        if (!clickedShapeKey) {
          setSelectedShapeKey(undefined);
          onBackgroundPointerDown?.(event);
        } else if (clickedShapeKey in drawingShapes && clickedShapeKey !== selectedShapeKey)
          setSelectedShapeKey(clickedShapeKey);
        handleEditingPointerDown(point, clickedShapeKey);
        break;
      }
    }
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const point = getCanvasCoordsFromEvent(event, event.currentTarget);
    switch (mode) {
      case DrawingCanvasMode.DRAW:
        handleDrawingPointerMove(point);
        return;
      case DrawingCanvasMode.EDIT:
        handleEditingPointerMove(point, getShapeKeyAt(point));
        return;
    }
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (event.button !== 0) return;
    const point = getCanvasCoordsFromEvent(event, event.currentTarget);
    switch (mode) {
      case DrawingCanvasMode.DRAW:
        handleDrawingPointerUp(point);
        break;
      case DrawingCanvasMode.EDIT:
        handleEditingPointerUp();
        break;
    }
  };

  const handlePointerCancel = () => {
    if (mode === DrawingCanvasMode.EDIT) handleEditingPointerCancel();
  };

  const handlePointerLeave = () => {
    if (mode === DrawingCanvasMode.DRAW) {
      handleDrawingPointerLeave();
      return;
    }
    handleEditingPointerLeave();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLCanvasElement>) => {
    if (mode === DrawingCanvasMode.DRAW) {
      handleDrawingKeyDown(event);
      return;
    }
    handleEditingKeyDown(event);
  };

  useEffect(() => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    ctx.reset();
    scaleCanvasForHighResDisplay(canvasRef.current, width, height);

    sortedDrawingShapes.forEach((shape) => {
      switch (shape.type) {
        case DrawingCanvasShapeType.LINE:
          renderLine(shape.points, ctx, shape.style);
          break;
        case DrawingCanvasShapeType.POINT:
        case DrawingCanvasShapeType.BBOX_CORNER:
        case DrawingCanvasShapeType.VERTEX:
          if (shape.points.length === 0) break;
          renderPoint(shape.points[0], ctx, shape.style);
          break;
        case DrawingCanvasShapeType.POLYGON:
        case DrawingCanvasShapeType.BBOX:
          renderPolygon(shape.points, ctx, shape.style);
      }
    });

    if (mode === DrawingCanvasMode.DRAW) {
      if (draftPoints.length > 0) {
        renderLine(draftPoints, ctx);
        if (cursorPoint) {
          renderLine([draftPoints[draftPoints.length - 1], cursorPoint], ctx, { dashed: true });
          if (drawingModeShapeType === DrawingCanvasShapeType.POLYGON) {
            renderPolygon([...draftPoints, cursorPoint], ctx, { lineWidth: 0 });
            renderLine([draftPoints[0], cursorPoint], ctx, { dashed: true });
          }
        }
        for (const point of draftPoints) renderPoint(point, ctx);
      }
      // Where a click would add a point
      if (cursorPoint) renderPoint(cursorPoint, ctx);
    }
    // Where a click would add a vertex to the hovered selected line or polygon
    if (mode === DrawingCanvasMode.EDIT && newVertexPoint) renderPoint(newVertexPoint, ctx);
  }, [sortedDrawingShapes, width, height, draftPoints, cursorPoint, newVertexPoint, mode, drawingModeShapeType]);

  return (
    <div className={''}>
      <canvas
        {...props}
        ref={canvasRef}
        role="img"
        // Clicking focuses the canvas so it gets key presses, but Tab skips it
        tabIndex={-1}
        width={width}
        height={height}
        data-slot="drawing-canvas"
        className={cn(
          (mode === DrawingCanvasMode.DRAW || (mode === DrawingCanvasMode.EDIT && newVertexPoint)) &&
            'cursor-crosshair',
          mode === DrawingCanvasMode.EDIT && hoveredKey && 'cursor-pointer',
          className,
        )}
        onKeyDown={handleKeyDown}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
        onPointerLeave={handlePointerLeave}
      />
      <HitDetectionCanvas
        ref={hitCanvasRef}
        width={width}
        height={height}
        sortedDrawingShapes={sortedDrawingShapes}
        shapeKeyToColorMap={keysToColors}
        selectedShapeKey={selectedShapeKey}
        className={showHitCanvas ? className : 'hidden'}
      />
    </div>
  );
};
